import { COLORS } from '@btime/constants'
import { isSortActive, propOrPath } from '@btime/helpers'
import React, { Fragment, useEffect } from 'react'

import { OpenCloseHandler, useOpenCloseState } from '@btime/react-contexts'
import { SmallText } from '../presentation/Text'
import { SubItem, SubItemsWrapper } from '../structure/SubItems'
import { TableColumn } from './Table'
import { useChangeSort, useSort } from '@btime/react-contexts'
import { useIsUsingMultiSelect, useTableActions, useTableState } from './MultiSelectTableContext'
import Checkbox from '../shapes/Checkbox'
import FlexGrid from '../structure/FlexGrid'
import FlexTable, { Col, Row } from './FlexTable'
import Link from '../actions/Link'
import RenderOnScreen from '../helpers/RenderOnScreen'
import Separator from '../helpers/Separator'
import SortIndicator from '../shapes/SortIndicator'
import Sticky from '../helpers/Sticky'

// TODO: Refacto and separate into multiple files
const getColRender = col => col.render || ((val, record, i) => <TableColumn>{val}</TableColumn>)

const renderHeadCols = (columns, sort, setSort) => {
  return columns.map(({ title, headerSideContent, flex, width, dataIndex, sortIndex }) => {
    const sortField = sortIndex || dataIndex
    const canSort = sortIndex !== false && dataIndex && setSort
    const isSortingField = isSortActive(sortField, sort || {})

    return (
      <Col key={title} width={width} flex={flex === undefined ? 1 : flex} head>
        <FlexGrid gutter={0.15} justify="flex-start">
          <Link block onClick={() => canSort && setSort(sortField)}>
            <SmallText bold={isSortingField} color={COLORS.dark3} breakWord>
              {title}
            </SmallText>
          </Link>
          <SortIndicator active={isSortingField} type={sort?.type} />
          {headerSideContent}
        </FlexGrid>
      </Col>
    )
  })
}

const renderCols = (item, index, rowKey, columns) => {
  return columns.map(({ title, flex, width, dataIndex, ...col }) => (
    <Col key={item[rowKey] + title} width={width} flex={flex === undefined ? 1 : flex}>
      {getColRender(col)(propOrPath('', dataIndex, item), item, index)}
    </Col>
  ))
}

const DataRow = ({
  item,
  index,
  rowKey,
  columns,
  getLeftBorderColor,
  renderChildren,
  disableMultiSelect,
  useSubElementSeparator
}) => {
  const canMultiSelect = useIsUsingMultiSelect() && !disableMultiSelect
  const { selecteds, isForcingSelection } = useTableState()
  const { handleSelect } = useTableActions()
  const open = useOpenCloseState()

  return (
    <div>
      <RenderOnScreen>
        <SubItem disabled={!useSubElementSeparator}>
          <Row leftBorderColor={getLeftBorderColor(item, index)}>
            {canMultiSelect && (
              <Col flex={0}>
                <Checkbox
                  tourStep="selectRow"
                  onChange={handleSelect(item[rowKey])}
                  active={isForcingSelection || selecteds.includes(item[rowKey])}
                  color={isForcingSelection && COLORS.red}
                />
              </Col>
            )}
            {renderCols(item, index, rowKey, columns)}
          </Row>
        </SubItem>
      </RenderOnScreen>

      {open && renderChildren && (
        <SubItemsWrapper>
          <Separator weight={4} />
          {renderChildren(item, index, rowKey)}
          <Separator weight={4} />
        </SubItemsWrapper>
      )}
    </div>
  )
}

const DataRowWrapper = ({ childrenStartsOpen, ...props }) => (
  <OpenCloseHandler initialValue={childrenStartsOpen}>
    <DataRow {...props} />
  </OpenCloseHandler>
)

const FlexDataTable = ({
  columns,
  data,
  renderChildren,
  rowKey,
  children,
  getLeftBorderColor,
  hideHeader,
  disableMultiSelect,
  useSubElementSeparator,
  checkChildrenStartsOpen
}) => {
  const sort = useSort()
  const setSort = useChangeSort()
  const canMultiSelect = useIsUsingMultiSelect() && !disableMultiSelect
  const { allSelected, isForcingSelection } = useTableState()
  const { selectAll, setData } = useTableActions()

  rowKey = rowKey || 'id'
  getLeftBorderColor = getLeftBorderColor || (() => {})

  // Pass the data and rowKey to the multi select context
  useEffect(() => {
    !disableMultiSelect && setData({ data, rowKey })
  }, [data])

  return (
    <Fragment>
      <FlexTable>
        {!hideHeader && (
          <Sticky>
            <Row head bgColor={COLORS.white}>
              {canMultiSelect && (
                <Col flex={0} head>
                  <Checkbox
                    tourStep="selectRows"
                    onChange={selectAll}
                    active={isForcingSelection || allSelected}
                    color={isForcingSelection && COLORS.red}
                  />
                </Col>
              )}
              {renderHeadCols(columns, sort, setSort)}
            </Row>
          </Sticky>
        )}

        {data.map((item, index) => (
          <DataRowWrapper
            key={item[rowKey]}
            item={item}
            index={index}
            rowKey={rowKey}
            columns={columns}
            getLeftBorderColor={getLeftBorderColor}
            renderChildren={renderChildren}
            disableMultiSelect={disableMultiSelect}
            useSubElementSeparator={useSubElementSeparator}
            childrenStartsOpen={checkChildrenStartsOpen && checkChildrenStartsOpen(item)}
          />
        ))}
      </FlexTable>
      {children}
    </Fragment>
  )
}

export default FlexDataTable
